import { useAnalytics } from '@ecosystem/newron-client-lib';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import FilterChips from '@components/filter-chips/FilterChips';
import FilterSelectionNote from '@components/filter-selection-note/FilterSelectionNote';
import ProductsTop5TableCard from '@components/products-top-5-table/ProductsTop5TableCard';
import { FocusArea, KpiTypesOption } from '@components/products-top-5-table/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import {
  initialTrendsKpi,
  ProductsCompareExport,
  ProductsParam,
  RetailerKpiOption,
  SaleType,
  TrendsKpi,
} from '@models/Products';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { BaseTimePeriod, MainFilters } from '@services/FiltersTypes';
import { getProductsTrends } from '@services/productTab/ProductsTrends.service';
import { IRootState } from '@state/reducers/Reducers';
import { SideFiltersState } from '@state/reducers/side-filters/SideFiltersReducer';
import {
  getFiltersInitialState,
  getInitialAppliedCountries,
} from '@utils/FilterUtils';
import { ErrorTagEnum, saveErrorLog } from '@utils/LogUtil';
import { getCorrespondingLatestPeriod, isPeriodYTDOrMAT } from '@utils/PeriodUtils';
import {
  PAGE_VIEW_TRACKING_TITLE,
  pageViewTrackingFunction,
  PageViewTrackingFunctionParam,
} from '@utils/page-analytics/model/PageViewedTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  Country,
  CountryFilterPair,
  FactRevenue,
  FeatureOption,
  FilterKeys,
  Filters,
  Option,
  PageName,
  PageType,
  SubSection,
} from '@utils/pageConfig/Types';
import { constructProductsParam } from '@utils/serviceUtils/RequestFormatter';
import {
  applyTimeFramePickerProductsCallback,
  countriesToQuery,
  getCurrentRetailerClientState,
  loadingDataPreparation,
  ProductsParamArgumentObject,
  ProductType,
} from '../Product';
import { getProductsCompareExport } from '@services/productTab/ProductsCompare.service';
import { PeriodFormat } from '@components/filter-selection-note/GetComparisonPeriod';
import { useTrendsExport, UseTrendsExportParam } from './UseTrendsExport';
import TimeFramePickerWrapper from '@components/time-frame-picker/components/wrapper/TimeFramePickerWrapper';
import { bindActionCreators } from 'redux';
import { actionCreatorsHitlistsRetailerClient, actionCreatorsProducts } from '@state/Index';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import useDebouncedEffect from '@hooks/UseDebouncedEffect';
import { containers } from '@ecosystem/newron-design-system';
import ProductsChartsFilter from '@components/products-line-charts/products-charts-filter/ProductsChartsFilter';
import ProductsMarketCard from '@pages/product-analyzer/trends/products-market-card/ProductsMarketCard';
import ProductsRetailerCard from '@pages/product-analyzer/trends/products-retailer-card/ProductsRetailerCard';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { CollectionDataType } from '@ecosystem/newron-client-collections/build/shared/types';
import { PRODUCT_TRENDS_CARD_TITLE } from '@pages/Utilities';
import { matchKPI } from '@utils/hook/sorting/UsePersistedSorting';
import { kpiDropdownOptions } from '@models/PerformanceAnalysisboard';
import { DEFAULT_VOLUME, getVolumeBasedOnPeriodType } from '@utils/factVolumeUtils/FactVolumeUtils';
import { ProductsViewState } from '@models/SavedViews';
import { ProductsMarketRetailerLineChartInsight } from '@components/collections/products-trends-market-and-retailer-line-chart/Model';
import { ProductsMarketRetailerLineChartInsightParams } from '@data-visualization/products-market-line-chart/Types';
import { getCountryFilterPairsByTimeFrame } from '@utils/CountryUtils';
import { setApiErrorState } from '@services/utils/ErrorHandlers';

interface Props {
  filters: MainFilters;
  timeFrame: Periodicity;
  period: BaseTimePeriod;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  setTimeFrame: Dispatch<SetStateAction<Periodicity>>;
  setPeriod: Dispatch<SetStateAction<BaseTimePeriod>>;
  setComparisonPeriod: Dispatch<SetStateAction<RelativePeriod | BaseTimePeriod>>;
  selectedProductGroup: Option[] | undefined;
  appliedCountries: Option[] | undefined;
  timeFrameOnChangeHandler: any;
  pgListByCountry: CountryFilterPair[];
  factRevenue: FactRevenue;
  currentProductType: ProductType;
  selectedKpi: KpiTypesOption;
  setSelectedKpi: Dispatch<SetStateAction<KpiTypesOption>>;
  selectedFocusArea: FocusArea;
  setSelectedFocusArea: Dispatch<SetStateAction<FocusArea>>;
  selectedSaleType: SaleType;
  setSelectedSaleType: Dispatch<SetStateAction<SaleType>>;
  selectedLineChartSaleType: SaleType;
  setSelectedLineChartSaleType: Dispatch<SetStateAction<SaleType>>;
  selectedRetailerOption: RetailerKpiOption;
  setSelectedRetailerOption: Dispatch<SetStateAction<RetailerKpiOption>>;
  isMultibannerChanging: boolean;
  defaultFilters: MainFilters[];
  prodLeaderboardFeatureProductFilters: Filters[];
  productsViewState: ProductsViewState;
  feature: FeatureOption;
}

const { Card } = containers;

const Trends: React.FC<Props> = ({
  filters,
  timeFrame,
  period,
  comparisonPeriod,
  setTimeFrame,
  setPeriod,
  setComparisonPeriod,
  selectedProductGroup,
  appliedCountries,
  timeFrameOnChangeHandler,
  pgListByCountry,
  factRevenue,
  currentProductType,
  selectedKpi,
  setSelectedKpi,
  selectedFocusArea,
  setSelectedFocusArea,
  selectedSaleType,
  setSelectedSaleType,
  selectedLineChartSaleType,
  setSelectedLineChartSaleType,
  selectedRetailerOption,
  setSelectedRetailerOption,
  isMultibannerChanging,
  defaultFilters,
  prodLeaderboardFeatureProductFilters,
  productsViewState,
  feature,
}) => {
  // Redux
  const pagesConfig = usePageConfig();

  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.RI_PRODUCT);

  const dispatch = useDispatch();
  const { updateMainFiltersProducts } = bindActionCreators(actionCreatorsProducts, dispatch);
  const { updateHitlistsRetailerClient } = bindActionCreators(actionCreatorsHitlistsRetailerClient, dispatch);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const hitlistsRetailerClientState = useSelector((state: any) => state.hitlistsRetailerClient);
  const sellerInsightsRetailerClientState = useSelector((state: any) => state.sellerInsightsRetailerClient);

  const currentRetailerClientState = getCurrentRetailerClientState(
    currentProductType,
    retailerClientState,
    hitlistsRetailerClientState,
    sellerInsightsRetailerClientState,
  );

  const mainFilterStateProducts = useSelector((state: any) => state.mainFiltersProducts);

  const { track } = useAnalytics();
  const sideFiltersState: SideFiltersState = useSelector((state: any) => state.sideFilters);

  const [isApiError, setIsApiError] = useState<boolean>(false);
  const [trendsKpi, setTrendsKpi] = useState<TrendsKpi>(initialTrendsKpi);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExportButtonDisabled, setIsExportButtonDisabled] = useState<boolean>(true);
  const [productsParam, setProductsParam] = useState<ProductsParam>();
  const [productsCompareExportData, setProductsCompareExportData] = useState<ProductsCompareExport[]>([]);
  const [volume, setVolume] = useState<string>(DEFAULT_VOLUME);
  const { clearExportImage } = useExportImages(PageType.RI_PRODUCT);

  const groupedTop5FilterState = {
    selectedKpi: selectedKpi,
    setSelectedKpi: setSelectedKpi,
    selectedFocusArea: selectedFocusArea,
    setSelectedFocusArea: setSelectedFocusArea,
    selectedSaleType: selectedSaleType,
    setSelectedSaleType: setSelectedSaleType,
  };

  // Should only call amplitude tracking once after the page is rendered
  useEffect(() => {
    //Update page viewing
    updateHitlistsRetailerClient({
      ...hitlistsRetailerClientState,
      pageViewing: PageName.RI_PRODUCT,
    });

    //Start Page View Amplitude Tracking
    const trackingParam: PageViewTrackingFunctionParam = {
      eventTitle: PAGE_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: hitlistsRetailerClientState,
      track: track,
      pageType: PageType.RI_PRODUCT,
      pageName: PageName.RI_PRODUCT,
      access: pagesConfig.access,
      subSection: SubSection.PRODUCT_TRENDS,
    };

    pageViewTrackingFunction(trackingParam);
    //End Page View Amplitude Tracking
  }, []);

  useDebouncedEffect(() => {
    if (!isMultibannerChanging) {
      loadingDataPreparation(pagesConfig, isLoading, defaultFilters, timeFrame, loadData, effectiveProductType);
    }
  }, [
    period,
    comparisonPeriod,
    pagesConfig.appliedFilters,
    pagesConfig.isRetailerChanged,
    selectedKpi,
    selectedFocusArea,
    selectedSaleType,
    isMultibannerChanging,
  ]);

  useEffect(() => {
    setVolume(getVolumeBasedOnPeriodType(filters, timeFrame));
  }, [filters, timeFrame]);

  const loadData = async (responseTimeFrame: any, countriesToQueryObject: Country[], pg: Option) => {
    if (responseTimeFrame !== null && selectedProductGroup) {
      try {
        setIsLoading(true);
        setIsExportButtonDisabled(true);
        setIsApiError(false);

        const productsParamObject: ProductsParamArgumentObject = {
          productType: effectiveProductType,
          pageNumber: 0,
          productGroupParam: pg,
          pagesConfig: pagesConfig,
          period: period,
          mainFilterStateProducts: mainFilterStateProducts,
          retailerClientState: currentRetailerClientState,
          countriesToQuery: countriesToQueryObject,
          timeFrame: timeFrame,
          defaultPageSize: 0,
          selectedSaleType: selectedSaleType,
          sortingKpi: selectedKpi.value,
          isMarket: selectedFocusArea,
          growthType: undefined,
          comparisonPeriod: comparisonPeriod,
        };
        const productsParameter = constructProductsParam(productsParamObject);
        if (!productsParameter) {
          return;
        }
        const trendsKpiData = await getProductsTrends(productsParameter);
        setTrendsKpi(trendsKpiData);
        setProductsParam(productsParameter);
        if (trendsKpiData.items.length > 0) {
          const productsParamExport = {
            ...productsParameter,
            itemIdList: trendsKpiData.items.map((itemData) => itemData.item.itemId),
            exportCompareComparisonType: comparisonPeriod,
          };
          const exportData = await getProductsCompareExport(productsParamExport);
          setProductsCompareExportData(exportData.slice(1));
          setIsExportButtonDisabled(false);
        } else {
          setIsExportButtonDisabled(true);
        }
      } catch (err: any) {
        console.error(err);
        saveErrorLog(err, 'Products Trends - Unable to get products trends/product compare export api response.', {
          category: ErrorTagEnum.ApiError,
          requestPayload: {
            clientId: retailerClientState.clientId,
            productGroup: selectedProductGroup,
            period: period,
            comparisonPeriod: comparisonPeriod,
          },
        });
        //Set back to initial state, to show no result if error occur.
        setTrendsKpi(initialTrendsKpi);
        setIsExportButtonDisabled(true);
        setApiErrorState(err, setIsApiError);
      } finally {
        // Listen and used to prevent multiple api calling , and set false sequence is important
        pagesConfig.setIsRetailerChanged(false);
        setIsLoading(false);
        clearExportImage();
      }
    } else {
      setTrendsKpi(initialTrendsKpi);
      setIsLoading(false);
    }
  };

  const getKpi = () => matchKPI(pagesConfig.sortingOptions, kpiDropdownOptions(volume));

  const getTrendsChartCollectionParams = (): ProductsMarketRetailerLineChartInsightParams => {
    let kpi = getKpi();
    const appliedFilters = [...pagesConfig.appliedFilters[PageType.RI_PRODUCT].selectedFilters];
    const pgArray = productsParam?.productGroup;
    let productGroupArray: Option[] = [];
    if (pgArray) {
      productGroupArray = Array.isArray(pgArray) ? pgArray : [pgArray];
    }
    return {
      clientId: retailerClientState.clientId,
      productType: effectiveProductType,
      productGroup: productGroupArray,
      countries: countriesToQuery(pagesConfig, appliedCountries ?? [], currentProductType),
      period: period.value,
      comparisonPeriod: comparisonPeriod,
      periodType: timeFrame,
      selectedGrowthType: mainFilterStateProducts?.selectedGrowthType,
      saleType: kpi.salesType,
      appliedFilters: appliedFilters,
      kpi: kpi.value,
      segment: feature.feature,
      dataType: feature.dataType,
      isSummary: false,
      pageName: pagesConfig.pageName,
    };
  };

  //Export Functionalities for products compare modal
  const useProductsTrendsParam: UseTrendsExportParam = {
    productsParam,
    mainFilterStateProducts,
    productsCompareExport: productsCompareExportData,
    pagesConfig,
    appliedCountries: getInitialAppliedCountries(pagesConfig, PageType.RI_PRODUCT, currentProductType),
    appliedPg: getFiltersInitialState(
      pagesConfig.appliedFilters[PageType.RI_PRODUCT].selectedFilters,
      FilterKeys.PRODUCT_GROUP,
    )?.options,
    factRevenue,
    timeFrame,
    selectedSalesType: selectedLineChartSaleType,
    selectedRetailerOption,
    comparisonPeriod,
    selectedKpi,
    latestPeriod: getCorrespondingLatestPeriod(defaultFilters, pagesConfig, timeFrame),
    trendsKpi,
  };

  const onChartDownload = useTrendsExport(useProductsTrendsParam);

  const exportChartParam = { onChartDownload, isExportButtonDisabled };

  const createLineChartInsightDataFn: CreateInsightDataFn<ProductsMarketRetailerLineChartInsight> = (
    collectionDataType: CollectionDataType = 'live',
  ) => {
    const trendsChartCollectionsApiParamProps = getTrendsChartCollectionParams();
    const getSelectedSaleType = (
      Object.values(SaleType).includes(selectedLineChartSaleType)
        ? selectedLineChartSaleType
        : Object.values(SaleType)[0]
    );
    return {
      apiParams: {
        ...trendsChartCollectionsApiParamProps,
        kpi: getSelectedSaleType,
        productsParam: productsParam,
      },
      insightCardTitle: PRODUCT_TRENDS_CARD_TITLE,
      selectedLineChartSaleType: getSelectedSaleType,
      selectedRetailerKpiOption: selectedRetailerOption,
      selectedFeature: feature,
      currencySymbol: factRevenue.currencySymbol,
      retailerClientName: retailerClientState.clientName,
      collectionDataType: collectionDataType,
    };
  };

  const filteredScopeData = getCountryFilterPairsByTimeFrame(pagesConfig.pgsByCountry, timeFrame);

  const applyTimeFramePickerCallback: (
    appliedPeriodicity: Periodicity,
    appliedPeriod: BaseTimePeriod,
    appliedRelativePeriod: RelativePeriod | BaseTimePeriod,
  ) => void = (appliedPeriodicity, appliedPeriod, appliedRelativePeriod) => {
    applyTimeFramePickerProductsCallback({
      appliedPeriodicity,
      appliedPeriod,
      appliedRelativePeriod,
      pagesConfig,
      mainFilterStateProducts,
      sideFiltersState,
      retailerClientState: hitlistsRetailerClientState,
      track,
      setTimeFrame,
      setPeriod,
      setRelativePeriod: setComparisonPeriod,
      updateMainFiltersProducts,
    });
  };

  return (
    <>
      <div className="grid grid-cols-4 gap-x-2 pt-6 pb-4">
        <TimeFramePickerWrapper
          currentPeriodicity={timeFrame}
          currentPeriod={period}
          currentComparison={comparisonPeriod}
          periodOptions={filters?.timePeriod}
          applyPickerCallback={applyTimeFramePickerCallback}
          isTimeFramePickerDisabled={isLoading}
          isPeriodOptionsDisabled={false}
          periodFormat={PeriodFormat.LONG}
          isPeriodOptionsDisabledPredicate={(periodicity) => ![Periodicity.YTD, Periodicity.MAT].includes(periodicity)}
          isHideComparisonPeriodsPanelPredicate={(periodicity) =>
            [Periodicity.YTD, Periodicity.MAT].includes(periodicity)
          }
          isDisplayNoComparisonLabel={isPeriodYTDOrMAT(period)}
        />
      </div>
      <div className="pb-4">
        <FilterSelectionNote
          selectedPeriodicity={timeFrame}
          selectedPeriod={period}
          comparisonPeriodType={comparisonPeriod}
          periodFormat={PeriodFormat.LONG}
          isDisplayNoComparisonLabel={isPeriodYTDOrMAT(period)}
          scopeMetadata={filteredScopeData[0]?.scopeMetadata}
        />
      </div>
      <div>
        <div className="pt-4">
          <FilterChips pgListByCountry={pgListByCountry} selectedTimeFrame={timeFrame} />
        </div>
      </div>

      <div className="pt-6">
        <ProductsTop5TableCard
          trendsKpi={trendsKpi}
          groupedTop5FilterState={groupedTop5FilterState}
          factRevenue={factRevenue}
          isLoading={isLoading}
          prodLeaderboardFeatureProductFilters={prodLeaderboardFeatureProductFilters}
          isApiError={isApiError}
        />
      </div>

      <div className="pt-6">
        <Card>
          <ProductsChartsFilter
            selectedLineChartSaleType={selectedLineChartSaleType}
            setSelectedLineChartSaleType={setSelectedLineChartSaleType}
            selectedShareOption={selectedRetailerOption}
            setSelectedShareOption={setSelectedRetailerOption}
            factRevenue={factRevenue}
            isLoading={isLoading}
            marketExportExcelParam={exportChartParam} // Market use
            createInsightDataFn={createLineChartInsightDataFn} // Retailer use
            productsViewState={productsViewState}
          />
          <div key={`${selectedLineChartSaleType}`}>
            <ProductsMarketCard
              trendsKpi={trendsKpi}
              selectedPeriodicity={timeFrame}
              selectedLineChartSaleType={selectedLineChartSaleType}
              setSelectedLineChartSaleType={setSelectedLineChartSaleType}
              factRevenue={factRevenue}
              isLoading={isLoading}
              pageTabName={PageName.RI_PRODUCT_TRENDS}
              isPeriodYTDorMAT={isPeriodYTDOrMAT(period)}
              subSectionForAmplitudeTracking={SubSection.PRODUCT_TRENDS}
              isApiError={isApiError}
            />
            <ProductsRetailerCard
              trendsKpi={trendsKpi}
              selectedPeriodicity={timeFrame}
              selectedLineChartSaleType={selectedLineChartSaleType}
              setSelectedLineChartSaleType={setSelectedLineChartSaleType}
              factRevenue={factRevenue}
              isLoading={isLoading}
              selectedOption={selectedRetailerOption}
              setSelectedOption={setSelectedRetailerOption}
              pageTabName={PageName.RI_PRODUCT_TRENDS}
              subSectionForAmplitudeTracking={SubSection.PRODUCT_TRENDS}
              isApiError={isApiError}
            />
          </div>
        </Card>
      </div>
    </>
  );
};

export default Trends;
